import { http } from "./config";

/**
 * Tiện ích tải file Excel dùng chung cho toàn hệ thống.
 * BE trả file (blob) theo template chung; FE chỉ gọi endpoint và tải xuống.
 */

// Lấy tên file từ header Content-Disposition, fallback nếu không có.
export const extractFileName = (response, fallback) => {
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

// Kích hoạt trình duyệt tải blob nhận được.
export const triggerBlobDownload = (response, fallbackName) => {
  const fileName = extractFileName(response, fallbackName);
  const blob = new Blob([response.data], {
    type:
      response?.headers?.["content-type"] ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

// Lỗi trả về khi responseType=blob nằm trong blob JSON -> đọc & bóc message.
const parseBlobError = async (error) => {
  const fallback = "Không thể xuất file. Vui lòng thử lại!";
  const data = error?.response?.data;
  if (!data || typeof data.text !== "function") {
    return error?.message || fallback;
  }
  try {
    const parsed = JSON.parse(await data.text());
    if (parsed?.errors) {
      const firstKey = Object.keys(parsed.errors)[0];
      const firstMsg = parsed.errors[firstKey];
      if (Array.isArray(firstMsg) && firstMsg.length) return firstMsg[0];
    }
    return parsed?.message || parsed?.error || fallback;
  } catch (_) {
    return fallback;
  }
};

/**
 * Gọi endpoint export và tải file về.
 * @param {string} url        Đường dẫn API (tương đối với baseURL).
 * @param {object} opts
 * @param {object} opts.params    Query params.
 * @param {string} opts.fileName  Tên file fallback (nên có đuôi .xlsx).
 * @param {number} opts.timeout   Timeout riêng (mặc định 120s cho file lớn).
 * @param {string} opts.method    'get' | 'post' (mặc định get).
 * @param {object} opts.data      Body khi method=post.
 * @returns {Promise<void>} throw kèm message dễ đọc nếu lỗi.
 */
export const downloadExcel = async (url, opts = {}) => {
  const {
    params,
    fileName = "export.xlsx",
    timeout = 120000,
    method = "get",
    data,
  } = opts;

  try {
    const config = { responseType: "blob", timeout, params };
    const response =
      method === "post"
        ? await http.post(url, data, config)
        : await http.get(url, config);
    triggerBlobDownload(response, fileName);
  } catch (error) {
    const messageText = await parseBlobError(error);
    const wrapped = new Error(messageText);
    wrapped.original = error;
    throw wrapped;
  }
};
